import { useEffect, useRef, useState } from 'react';

/**
 * Declarative HMI field renderer.
 *
 * Widget CHOICE is driven purely by `hmiResolved.widget` + `dataType`/`enumOptions`
 * — never by `fieldKey`. That is the whole point of the revamp: a brand-new
 * machine-type field renders with the right control and no code change. The only
 * thing keyed by `fieldKey` is the value binding (read `values[fieldKey]`, write
 * back through the same key) — which is data binding, not widget selection.
 */

function parseEnumOptions(enumOptions, current) {
  let opts = [];
  try {
    const parsed = typeof enumOptions === 'string' ? JSON.parse(enumOptions) : enumOptions;
    if (Array.isArray(parsed)) opts = parsed;
    else if (parsed && typeof parsed === 'object') opts = Object.keys(parsed);
  } catch {
    opts = [];
  }
  if (opts.length === 0 && current != null) opts = [String(current)];
  return opts.map((o) => String(o));
}

/** A large CRT readout: amber label above, glowing green value. */
function Readout({ label, value, unit, big }) {
  let display = value;
  if (display != null && typeof display === 'object') display = JSON.stringify(display);
  return (
    <div className="fanuc-cell px-3 py-2 flex flex-col gap-0.5 min-w-0">
      <span className="fanuc-label text-[10px] uppercase truncate">{label}</span>
      <span className={`fanuc-value font-bold tabular-nums break-all ${big ? 'text-2xl' : 'text-base'}`}>
        {display == null || display === '' ? '—' : String(display)}
        {unit ? <span className="fanuc-label text-xs ml-1">{unit}</span> : null}
      </span>
    </div>
  );
}

/** CRT slider — big value + horizontal bar the operator can drag. */
function CrtSlider({ label, unit, value, min, max, step, disabled, onChange }) {
  const num = Number.isFinite(Number(value)) ? Number(value) : min;
  return (
    <div className="fanuc-cell px-3 py-2 flex flex-col gap-1 min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="fanuc-label text-[10px] uppercase truncate">{label}</span>
        <span className="fanuc-value font-bold text-lg tabular-nums">
          {Math.round(num)}
          {unit ? <span className="fanuc-label text-xs ml-0.5">{unit}</span> : null}
        </span>
      </div>
      <input
        type="range"
        className="fanuc-range w-full"
        min={min}
        max={max}
        step={step}
        value={num}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

/** CRT toggle for boolean fields, or a two-state enum flip. */
function CrtToggle({ label, on, disabled, onToggle }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onToggle}
      aria-pressed={on}
      className="fanuc-cell px-3 py-2 flex items-center justify-between gap-2 w-full min-w-0 disabled:opacity-60"
    >
      <span className="fanuc-label text-[10px] uppercase truncate">{label}</span>
      <span
        className="fanuc-value font-bold text-sm px-2 py-0.5 rounded border"
        style={{ borderColor: 'currentColor', opacity: on ? 1 : 0.55 }}
      >
        {on ? 'ON' : 'OFF'}
      </span>
    </button>
  );
}

/** CRT select for enum fields. */
function CrtSelect({ label, value, options, disabled, datalist, onChange }) {
  return (
    <label className="fanuc-cell px-3 py-2 flex flex-col gap-1 min-w-0">
      <span className="fanuc-label text-[10px] uppercase truncate">{label}</span>
      {datalist ? (
        <>
          <input
            list={`dl-${label}`}
            className="bg-black/40 border border-current/40 rounded px-2 py-1 fanuc-value font-mono text-sm w-full disabled:opacity-60"
            value={value ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          <datalist id={`dl-${label}`}>
            {options.map((o) => (
              <option key={o} value={o} />
            ))}
          </datalist>
        </>
      ) : (
        <select
          className="bg-black/40 border border-current/40 rounded px-2 py-1 fanuc-value font-mono text-sm w-full disabled:opacity-60"
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      )}
    </label>
  );
}

/** Editable text field (blur/Enter commits). */
function CrtText({ label, value, disabled, options, onChange }) {
  const [draft, setDraft] = useState(value ?? '');
  const dirty = useRef(false);
  useEffect(() => {
    if (!dirty.current) setDraft(value ?? '');
  }, [value]);
  const commit = () => {
    dirty.current = false;
    if (draft !== (value ?? '')) onChange(draft);
  };
  if (options && options.length) {
    return <CrtSelect label={label} value={value} options={options} datalist disabled={disabled} onChange={onChange} />;
  }
  return (
    <label className="fanuc-cell px-3 py-2 flex flex-col gap-1 min-w-0">
      <span className="fanuc-label text-[10px] uppercase truncate">{label}</span>
      <input
        type="text"
        className="bg-black/40 border border-current/40 rounded px-2 py-1 fanuc-value font-mono text-sm w-full disabled:opacity-60"
        value={draft}
        disabled={disabled}
        onChange={(e) => {
          dirty.current = true;
          setDraft(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
    </label>
  );
}

/** Integer preset (e.g. counter): value + step/reset. */
function CrtCounter({ label, value, disabled, onSet }) {
  const n = Number(value ?? 0);
  return (
    <div className="fanuc-cell px-3 py-2 flex items-center justify-between gap-2 min-w-0">
      <div className="flex flex-col min-w-0">
        <span className="fanuc-label text-[10px] uppercase truncate">{label}</span>
        <span className="fanuc-value font-bold text-2xl tabular-nums">{n}</span>
      </div>
      {!disabled && (
        <div className="flex gap-1 shrink-0">
          <button
            type="button"
            className="px-2 py-1 text-xs rounded border border-current/50 hover:bg-white/10"
            onClick={() => onSet(n + 1)}
          >
            +1
          </button>
          <button
            type="button"
            className="px-2 py-1 text-xs rounded border border-current/50 hover:bg-white/10"
            onClick={() => onSet(0)}
          >
            RST
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Render one field declaratively.
 * @param {object} p
 * @param {object} p.field  MachineStreamFieldDefinition + hmiResolved
 * @param {Record<string,unknown>} p.values live/mock state
 * @param {object} p.bridge action bridge
 * @param {string[]} [p.programOptions] program numbers for text/program-section fields
 * @param {boolean} p.interactive false → force read-only presentation
 */
export function FanucWidget({ field, values, bridge, programOptions, interactive }) {
  const h = field.hmiResolved || {};
  const key = field.fieldKey;
  const label = field.label || key;
  const cur = values[key];
  const widget = h.widget || 'readonly';
  // Interactivity is decided by the WIDGET (a 'readonly' widget has no control at
  // all), not by the field's `manuallyOverridable` flag — that flag is unset
  // (false) across the seed, and writes are already gated at the bridge:
  // writeFieldValue only routes supported keys and no-ops the rest. Design mode
  // makes every cell inert so clicks select instead of operate.
  const canWrite = interactive;

  const apply = (next) => bridge.apply(key, next);
  const applyDebounced = (next) => bridge.setValueDebounced(key, next);

  switch (widget) {
    case 'slider':
      return (
        <CrtSlider
          label={label}
          unit={field.unit || (h.max === 200 ? '%' : '')}
          value={cur}
          min={h.min ?? 0}
          max={h.max ?? 100}
          step={h.step ?? 1}
          disabled={!canWrite}
          onChange={applyDebounced}
        />
      );

    case 'toggle': {
      // boolean → ON/OFF; two-state enum → flip to the other option.
      if (field.dataType === 'enum') {
        const opts = parseEnumOptions(field.enumOptions, cur);
        const on = opts.length ? String(cur) === String(opts[opts.length - 1]) : !!cur;
        const flip = () => {
          if (opts.length >= 2) apply(on ? opts[0] : opts[opts.length - 1]);
        };
        return <CrtToggle label={`${label} (${cur ?? '—'})`} on={on} disabled={!canWrite} onToggle={flip} />;
      }
      return <CrtToggle label={label} on={!!cur} disabled={!canWrite} onToggle={() => apply(!cur)} />;
    }

    case 'enum':
      return (
        <CrtSelect
          label={label}
          value={cur ?? ''}
          options={parseEnumOptions(field.enumOptions, cur)}
          disabled={!canWrite}
          onChange={apply}
        />
      );

    case 'text':
      return (
        <CrtText
          label={label}
          value={cur}
          disabled={!canWrite}
          options={h.section === 'program' ? programOptions : undefined}
          onChange={apply}
        />
      );

    case 'preset':
      // Preset means "an important, primary readout with a quick action".
      // Numeric → a counter; enum/boolean start-stop is handled by the panel's
      // dedicated START/STOP control, so here we just show it as a readout.
      if (field.dataType === 'integer' || field.dataType === 'float') {
        return <CrtCounter label={label} value={cur} disabled={!canWrite} onSet={apply} />;
      }
      return <Readout label={label} value={cur} big />;

    case 'tlm_grid':
      // Rendered once at the section level by the panel; never per-field here.
      return null;

    case 'readonly':
    default:
      return <Readout label={label} value={cur} unit={field.unit} />;
  }
}

export { Readout };
